import { useState, useCallback } from 'react';
import { useHttp } from './useHttp';
import { normalizeRaffle } from 'src/utils/raffleHelpers';
import type { Raffle, PaginatedResponse, PaginationMeta, RaffleQueryParams } from 'src/types/raffle';

interface UseRafflesReturn {
  raffles: Raffle[];
  meta: PaginationMeta | null;
  isLoading: boolean;
  error: string | null;
  fetchRaffles: (params?: RaffleQueryParams) => void;
}

export function useRaffles(): UseRafflesReturn {
  const { isLoading, error, sendRequest } = useHttp();
  const [raffles, setRaffles] = useState<Raffle[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);

  const fetchRaffles = useCallback(
    (params?: RaffleQueryParams) => {
      const queryParams = new URLSearchParams();
      const p = params ?? {};

      if (p.status) queryParams.set('status', p.status);
      if (p.prizeType) queryParams.set('prizeType', p.prizeType);
      if (p.isFeatured !== undefined) queryParams.set('isFeatured', String(p.isFeatured));
      if (p.ticketPriceMin != null) queryParams.set('ticketPriceMin', String(p.ticketPriceMin));
      if (p.ticketPriceMax != null) queryParams.set('ticketPriceMax', String(p.ticketPriceMax));
      if (p.endsWithin) queryParams.set('endsWithin', p.endsWithin);
      if (p.search) queryParams.set('search', p.search);
      if (p.sortBy) queryParams.set('sortBy', p.sortBy);
      if (p.sortOrder) queryParams.set('sortOrder', p.sortOrder);
      if (p.page) queryParams.set('page', String(p.page));
      if (p.limit) queryParams.set('limit', String(p.limit));

      const qs = queryParams.toString();
      const url = `raffles${qs ? `?${qs}` : ''}`;

      sendRequest({ url }, (response: unknown) => {
        const res = response as PaginatedResponse<any>;
        setRaffles(res.data.map(normalizeRaffle));
        setMeta(res.meta);
      });
    },
    [sendRequest],
  );

  return { raffles, meta, isLoading, error, fetchRaffles };
}
